import { GetQSetByIdDocument, useChangeSingleQuestionMutation } from '@exam-management/graphql';
import { Form } from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { zodResolver, type PropsWithChildren } from '@shared/utils';
import { FormProvider, useForm } from 'react-hook-form';

import {
  type SingleQuestionChangeFormInput,
  SingleQuestionChangeSchema,
} from './single-question-change-schema';

interface SingleQuestionChangeFormProviderProps extends PropsWithChildren {
  qSetQuestionId: string;
  onComplete?: () => void;
}

export default function SingleQuestionChangeFormProvider(
  props: Readonly<SingleQuestionChangeFormProviderProps>,
) {
  const { children, qSetQuestionId, onComplete } = props;

  const { showSnackbar } = useSnackbar();

  const formContext = useForm({
    defaultValues: {
      questionId: undefined,
    },
    resolver: zodResolver(SingleQuestionChangeSchema),
  });

  const [qsetChangeSingleQuestion] = useChangeSingleQuestionMutation({
    onCompleted: (data) => {
      const order = data.excChangeSingleQuestion.qSetQuestion.order;
      const qId = data.excChangeSingleQuestion.qSetQuestion.questionRefCode;
      showSnackbar({
        message: `Question No.${order} has been replaced with ${qId} successfully`,
        variant: 'success',
      });
      onComplete?.();
    },
    onError: () => {
      showSnackbar({ message: 'Change question failed, please try again later', variant: 'error' });
    },
    awaitRefetchQueries: true,
    refetchQueries: [GetQSetByIdDocument],
  });

  const handleSingleQuestionChange = async (data: SingleQuestionChangeFormInput) => {
    await qsetChangeSingleQuestion({
      variables: {
        changeSingleQuestionInput: {
          qSetQuestionId: qSetQuestionId,
          questionId: data.questionId,
        },
      },
    });
  };

  return (
    <FormProvider {...formContext}>
      <Form onSubmit={formContext.handleSubmit(handleSingleQuestionChange)}>{children}</Form>
    </FormProvider>
  );
}
